import { formatCount, parseMoneyToCents, rateOf, tt } from '@agos/shared'

/**
 * `/analytics` — the whole business on one page, in five tabs.
 *
 * Fourteen endpoints, and they do not agree on how to name a date range:
 *
 * | Endpoint | Range parameters |
 * |---|---|
 * | `/analytics/revenue`, `/analytics/day-hour-revenue` | `from` / `to` **or** `days` |
 * | `/analytics/sla` | **`dateFrom` / `dateTo`** or `days` |
 * | `coupons-roi`, `margins`, `pizza-ingredients-margin`, `loyalty-stats`, `game-correlation`, `campaigns` | `days` only |
 * | `/analytics/cohorts` | `months` only |
 * | `/analytics/rfm`, `/analytics/rfm/customers`, `/analytics/time-to-first-order` | none |
 *
 * Sending the wrong pair does not fail — the parameter is ignored and the endpoint answers over its
 * own default window. Measured: `/analytics/sla?from=2026-06-01&to=2026-06-30` answers **2 366**
 * orders (its 30-day default) where `dateFrom`/`dateTo` answers **2 625** for June. So each shape has
 * its own function here and a test pins all three.
 */

// ─── the period, and what each endpoint will accept of it ────────────────────

export type PresetDays = 1 | 7 | 30 | 90 | 365

export interface Period {
  kind: 'preset' | 'custom'
  days: PresetDays
  from: string
  to: string
}

export function isoDay(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function daysBefore(today: Date, count: number): string {
  const shifted = new Date(today)
  shifted.setDate(shifted.getDate() - count)
  return isoDay(shifted)
}

export function initialPeriod(today: Date): Period {
  return { kind: 'preset', days: 90, from: daysBefore(today, 30), to: isoDay(today) }
}

/** Whether a custom period is usable — an empty or inverted range is not. */
export function periodIsUsable(period: Period): boolean {
  if (period.kind === 'preset') return true
  return period.from !== '' && period.to !== '' && period.from <= period.to
}

/** `/analytics/revenue` and `/analytics/day-hour-revenue`: `from`/`to`, or `days`. */
export function fromToParams(period: Period): { from?: string; to?: string; days?: number } {
  if (period.kind === 'custom' && periodIsUsable(period)) {
    return { from: period.from, to: period.to }
  }
  return { days: period.days }
}

/** `/analytics/sla`: `dateFrom`/`dateTo`, or `days`. The names are the contract. */
export function slaParams(period: Period): { dateFrom?: string; dateTo?: string; days?: number } {
  if (period.kind === 'custom' && periodIsUsable(period)) {
    return { dateFrom: period.from, dateTo: period.to }
  }
  return { days: period.days }
}

/**
 * **FB-115.** The six endpoints that take a day count and nothing else.
 *
 * Legacy computes an `effectiveDays` from the custom range and sends it as `days`. But `days` counts
 * back from `NOW()`, so choosing 1–30 June makes the coupon, margin and pizza cards report the *last*
 * thirty days — a window that barely overlaps the one the selector shows, under a header that says
 * *posledných 30 dní* while the selector says June.
 *
 * The day count is still the closest thing these endpoints accept, so it is still sent. What changes
 * is that the page stops pretending: `dayWindowNote` says the window is counted from today, and each
 * card is labelled with the `windowDays` the **server** answered rather than with what was asked.
 */
export function dayCountParams(period: Period): { days: number } {
  return { days: period.kind === 'custom' ? inclusiveDays(period) : period.days }
}

export function inclusiveDays(period: Period): number {
  if (period.kind === 'preset') return period.days
  if (!periodIsUsable(period)) return period.days
  const from = Date.parse(`${period.from}T00:00:00`)
  const to = Date.parse(`${period.to}T00:00:00`)
  if (!Number.isFinite(from) || !Number.isFinite(to)) return period.days
  return Math.max(1, Math.round((to - from) / 86_400_000) + 1)
}

export function periodLabel(period: Period): string {
  if (period.kind === 'custom' && periodIsUsable(period)) {
    return period.from === period.to ? period.from : `${period.from} → ${period.to}`
  }
  if (period.days === 1) return tt('dnes')
  if (period.days === 365) return tt('posledný rok')
  return `${tt('posledných')} ${formatCount(period.days)} ${tt('dní')}`
}

/** What a `days`-only card is really showing, said out loud. `null` when there is nothing to warn about. */
export function dayWindowNote(period: Period, serverWindowDays: number | undefined): string | null {
  if (period.kind !== 'custom' || !periodIsUsable(period)) return null
  const window = serverWindowDays ?? inclusiveDays(period)
  return `${tt('Tento údaj server počíta ako posledných')} ${formatCount(window)} ${tt('dní od dneška, nie za zvolené obdobie')}`
}

// ─── segments ────────────────────────────────────────────────────────────────

export interface SegmentRow {
  segment?: string
  count?: number
  avg_recency_days?: number | string
  avg_frequency?: number | string
  avg_monetary?: number | string
  total_revenue?: number | string
}

export interface AtRiskRow {
  id?: number
  name?: string | null
  email?: string | null
  phone?: string | null
  recency_days?: number
  frequency?: number
  monetary?: number | string
  last_order_at?: string | null
}

export interface RfmResponse {
  segments?: SegmentRow[]
  atRisk?: AtRiskRow[]
}

/**
 * Every number on this page arrives as a JSON number or a numeric string, and most of them are not
 * money — a percentage, a count of minutes, an average topping count. `rateOf` is the shared reader
 * for exactly that: it parses without rounding to the cent, which is what a multiplicand needs.
 */
export const num = rateOf

/** §14.7: money is compared and totalled in integer cents, never in floats. */
export const cents = parseMoneyToCents

export const SEGMENT_ORDER: readonly string[] = [
  'champions',
  'loyal',
  'newCustomers',
  'atRisk',
  'hibernating',
  'occasional',
  'lost',
]

export interface SegmentTotals {
  customers: number
  revenueCents: number
  champions: number
  atRisk: number
}

export function segmentTotals(segments: SegmentRow[]): SegmentTotals {
  const countOf = (name: string) => segments.find((row) => row.segment === name)?.count ?? 0
  return {
    customers: segments.reduce((sum, row) => sum + (row.count ?? 0), 0),
    revenueCents: segments.reduce((sum, row) => sum + cents(row.total_revenue), 0),
    champions: countOf('champions'),
    atRisk: countOf('atRisk'),
  }
}

/** Worst-to-best is not a useful order for a grid somebody scans; the funnel order is. */
export function orderSegments(segments: SegmentRow[]): SegmentRow[] {
  const rank = (name: string) => {
    const index = SEGMENT_ORDER.indexOf(name)
    return index === -1 ? SEGMENT_ORDER.length : index
  }
  return [...segments].sort((left, right) => rank(left.segment ?? '') - rank(right.segment ?? ''))
}

export function segmentShare(row: SegmentRow, totals: SegmentTotals): number {
  if (totals.customers === 0) return 0
  return ((row.count ?? 0) / totals.customers) * 100
}

/**
 * A segment with no orders has no average order — and `lost` is 506 of the 2 464 customers.
 *
 * The server answers `avg_frequency: 0` and `total_revenue: 0` for it, which is correct: they
 * registered and never ordered. But *recency* comes back as 988 days, which is days since
 * registration, not since an order that never happened. Rendering it as *988 dní od objednávky* states
 * something untrue, so it reads as registration age and says which it is.
 */
export function recencyMeaning(row: SegmentRow): 'since-order' | 'since-signup' {
  return num(row.avg_frequency) === 0 ? 'since-signup' : 'since-order'
}

// ─── revenue ─────────────────────────────────────────────────────────────────

export interface DailyRow {
  day?: string
  orders?: number
  revenue?: number | string
  avg_order_value?: number | string
}

export interface ChannelRow {
  type?: string
  orders?: number
  revenue?: number | string
  avg_order_value?: number | string
}

export interface DiscountRow {
  bucket?: string
  orders?: number
  revenue?: number | string
  avg_order_value?: number | string
  total_discount_given?: number | string
}

export interface RepeatRow {
  total_customers?: number
  repeat_customers?: number
  frequent_customers?: number
  avg_orders_per_customer?: number | string
}

export interface Headline {
  orders?: number
  revenue?: number | string
  avg_order_value?: number | string
  total_discount?: number | string
}

export interface RevenueResponse {
  windowDays?: number
  headline?: Headline
  dailyTrend?: DailyRow[]
  channelSplit?: ChannelRow[]
  discountImpact?: DiscountRow[]
  repeatRate?: RepeatRow
}

export const CHANNEL_ORDER: readonly string[] = ['delivery', 'restaurant', 'bar']

/**
 * Whether the four views of the same window agree.
 *
 * They do on this data — 153 704,55 € and 7 419 orders four times over, to the cent — and a report
 * that adds up is worth saying so about, because the day the channel split stops matching the headline
 * is the day somebody spends an afternoon on it. The same check FB-104 put on `/statistics`.
 */
export interface RevenueAgreement {
  headlineCents: number
  dayCents: number
  channelCents: number
  bucketCents: number
  agrees: boolean
  worstGapCents: number
  headlineOrders: number
  dayOrders: number
  channelOrders: number
  bucketOrders: number
  ordersAgree: boolean
}

export function checkRevenue(response: RevenueResponse): RevenueAgreement {
  const headlineCents = cents(response.headline?.revenue)
  const sum = (rows: { revenue?: number | string }[]) =>
    rows.reduce((total, row) => total + cents(row.revenue), 0)
  const count = (rows: { orders?: number }[]) =>
    rows.reduce((total, row) => total + (row.orders ?? 0), 0)

  const dayCents = sum(response.dailyTrend ?? [])
  const channelCents = sum(response.channelSplit ?? [])
  const bucketCents = sum(response.discountImpact ?? [])
  const worstGapCents = Math.max(
    Math.abs(headlineCents - dayCents),
    Math.abs(headlineCents - channelCents),
    Math.abs(headlineCents - bucketCents),
  )

  const headlineOrders = response.headline?.orders ?? 0
  const dayOrders = count(response.dailyTrend ?? [])
  const channelOrders = count(response.channelSplit ?? [])
  const bucketOrders = count(response.discountImpact ?? [])

  return {
    headlineCents,
    dayCents,
    channelCents,
    bucketCents,
    agrees: worstGapCents === 0,
    worstGapCents,
    headlineOrders,
    dayOrders,
    channelOrders,
    bucketOrders,
    ordersAgree:
      headlineOrders === dayOrders &&
      headlineOrders === channelOrders &&
      headlineOrders === bucketOrders,
  }
}

export interface ChannelSlice {
  type: string
  orders: number
  revenueCents: number
  avgCents: number
  sharePercent: number
}

export function channelSlices(rows: ChannelRow[]): ChannelSlice[] {
  const totalCents = rows.reduce((sum, row) => sum + cents(row.revenue), 0)
  const rank = (type: string) => {
    const index = CHANNEL_ORDER.indexOf(type)
    return index === -1 ? CHANNEL_ORDER.length : index
  }
  return [...rows]
    .sort((left, right) => rank(left.type ?? '') - rank(right.type ?? ''))
    .map((row) => ({
      type: row.type ?? '',
      orders: row.orders ?? 0,
      revenueCents: cents(row.revenue),
      // The server's own average, not revenue ÷ orders — the two differ in the last cent and the
      // server's is the one every other screen shows.
      avgCents: cents(row.avg_order_value),
      sharePercent: totalCents === 0 ? 0 : (cents(row.revenue) / totalCents) * 100,
    }))
}

export interface DiscountComparison {
  withDiscount: DiscountRow | undefined
  without: DiscountRow | undefined
  /** How much bigger a discounted order is, in percent, or `null` when either side is empty. */
  liftPercent: number | null
}

export function compareDiscounts(rows: DiscountRow[]): DiscountComparison {
  const withDiscount = rows.find((row) => row.bucket === 'with_discount')
  const without = rows.find((row) => row.bucket === 'without_discount')
  const withAvg = cents(withDiscount?.avg_order_value)
  const withoutAvg = cents(without?.avg_order_value)
  return {
    withDiscount,
    without,
    liftPercent:
      withAvg > 0 && withoutAvg > 0 ? ((withAvg - withoutAvg) / withoutAvg) * 100 : null,
  }
}

export interface RepeatShare {
  total: number
  repeat: number
  frequent: number
  repeatPercent: number | null
  frequentPercent: number | null
  avgOrders: number
}

/** A share of nobody is not 0 % — it is nothing, and says so. */
export function repeatShare(row: RepeatRow | undefined): RepeatShare {
  const total = row?.total_customers ?? 0
  const repeat = row?.repeat_customers ?? 0
  const frequent = row?.frequent_customers ?? 0
  return {
    total,
    repeat,
    frequent,
    repeatPercent: total === 0 ? null : (repeat / total) * 100,
    frequentPercent: total === 0 ? null : (frequent / total) * 100,
    avgOrders: num(row?.avg_orders_per_customer),
  }
}

// ─── day × hour ──────────────────────────────────────────────────────────────

export interface HeatCell {
  dow?: number
  hour?: number
  orders?: number
  revenue?: number | string
}

export interface HeatGrid {
  /** `[weekdayIndexMondayFirst][hour]` in cents. */
  rows: number[][]
  maxCents: number
  totalCents: number
  busiest: { dow: number; hour: number; cents: number } | null
}

/** Sunday is index 0 in the payload; a Slovak week starts on Monday. */
export const MONDAY_FIRST: readonly number[] = [1, 2, 3, 4, 5, 6, 0]

export function heatGrid(cells: HeatCell[]): HeatGrid {
  const byDow: number[][] = Array.from({ length: 7 }, () => Array.from({ length: 24 }, () => 0))
  let maxCents = 0
  let totalCents = 0
  let busiest: HeatGrid['busiest'] = null

  for (const cell of cells) {
    const dow = cell.dow ?? -1
    const hour = cell.hour ?? -1
    if (dow < 0 || dow > 6 || hour < 0 || hour > 23) continue
    const value = cents(cell.revenue)
    byDow[dow]![hour] = value
    totalCents += value
    if (value > maxCents) {
      maxCents = value
      busiest = { dow, hour, cents: value }
    }
  }

  return { rows: MONDAY_FIRST.map((dow) => byDow[dow] ?? []), maxCents, totalCents, busiest }
}

// ─── coupons ─────────────────────────────────────────────────────────────────

export interface CouponRow {
  code?: string
  type?: string
  value?: number | string
  orders_using?: number
  revenue?: number | string
  discount_given?: number | string
}

export interface CouponRoi {
  code: string
  type: string
  value: number
  uses: number
  revenueCents: number
  discountCents: number
  netCents: number
  /** Return per euro given away, as a percentage. `null` when nothing was given away. */
  roiPercent: number | null
}

/**
 * What a coupon returned against what it cost.
 *
 * `netCents` is revenue minus discount, which is legacy's figure and is kept. `roiPercent` is `null`
 * rather than 0 when the discount is nought: a coupon that cost nothing has no return *per euro*, and
 * legacy prints `(0%)` there, which reads as a coupon that returned nothing.
 */
export function couponRoi(rows: CouponRow[]): CouponRoi[] {
  return rows
    .map((row) => {
      const revenueCents = cents(row.revenue)
      const discountCents = cents(row.discount_given)
      const netCents = revenueCents - discountCents
      return {
        code: row.code ?? '',
        type: row.type ?? '',
        value: num(row.value),
        uses: row.orders_using ?? 0,
        revenueCents,
        discountCents,
        netCents,
        roiPercent: discountCents === 0 ? null : (netCents / discountCents) * 100,
      }
    })
    .sort((left, right) => right.netCents - left.netCents)
}

export function couponTotals(rows: CouponRoi[]): {
  revenueCents: number
  discountCents: number
  netCents: number
} {
  return rows.reduce(
    (totals, row) => ({
      revenueCents: totals.revenueCents + row.revenueCents,
      discountCents: totals.discountCents + row.discountCents,
      netCents: totals.netCents + row.netCents,
    }),
    { revenueCents: 0, discountCents: 0, netCents: 0 },
  )
}

// ─── margins ─────────────────────────────────────────────────────────────────

export interface MarginRow {
  id?: number
  name?: string
  price?: number | string
  estimated_cost?: number | string
  cost_source?: string
  units_sold?: number
  revenue?: number | string
  margin_pct?: number | string
  estimated_profit?: number | string
}

export type CostState = 'priced' | 'no-recipe' | 'recipe-unpriced'

/**
 * **FB-112.** Why a product's cost is missing, which is not one reason but two.
 *
 * The server answers `cost_source: "unknown"` when the product has no recipe, and `"recipe"` when it
 * has one — but a recipe made of stock items that carry no `cost_price` resolves to **0,00**, and the
 * server still calls that `recipe`. On the restored data, of the thirty products in a 30-day report:
 *
 * - **15** have no recipe at all
 * - **10** have a recipe whose ingredients are unpriced — badged *recept* in legacy, next to a 100 %
 *   margin
 * - **5** have a real cost
 *
 * The reason it is 10 and not 0 is upstream: **3 of the 135 stock items have a `cost_price`**, and all
 * three are the same flour. So legacy's footnote — *"Produkty bez receptu majú náklad —. Vyplňte
 * recepty + nákladovú cenu surovín"* — points at the wrong fix for ten of the twenty-five: they have
 * recipes. Telling the two apart is the whole difference between *write a recipe* and *price the stock*.
 *
 * **The server distinguishes them now** (`MIGRATION-STATUS`, 2026-08-02): it answers
 * `recipe_unpriced`, and `margin_pct` is `null` rather than 100 for both unpriced states. Measured
 * across all 164 active products: 79 `unknown`, **31 `recipe_unpriced`**, 54 priced. The inference
 * below is kept as the fallback — it costs one comparison and it is what an older server sends.
 */
export function costState(row: MarginRow): CostState {
  if (cents(row.estimated_cost) > 0) return 'priced'
  if (row.cost_source === 'recipe_unpriced') return 'recipe-unpriced'
  return row.cost_source === 'recipe' ? 'recipe-unpriced' : 'no-recipe'
}

export interface Margin {
  id: number
  name: string
  priceCents: number
  costCents: number
  state: CostState
  units: number
  revenueCents: number
  /** `null` unless the cost is known — a product with no cost has no margin, not a margin of 100 %. */
  marginPercent: number | null
  /** `null` unless the cost is known: revenue with nothing subtracted is not profit. */
  profitCents: number | null
}

export function margins(rows: MarginRow[]): Margin[] {
  return rows.map((row) => {
    const state = costState(row)
    const priceCents = cents(row.price)
    const revenueCents = cents(row.revenue)
    return {
      id: row.id ?? 0,
      name: row.name ?? '',
      priceCents,
      costCents: cents(row.estimated_cost),
      state,
      units: row.units_sold ?? 0,
      revenueCents,
      marginPercent: state === 'priced' ? num(row.margin_pct) : null,
      profitCents: state === 'priced' ? cents(row.estimated_profit) : null,
    }
  })
}

export type MarginSort = 'profit' | 'margin' | 'units'

/** A row whose profit is unknown sorts after every row whose profit is known, not above them. */
export function sortMargins(rows: Margin[], sort: MarginSort): Margin[] {
  const key = (row: Margin): number | null =>
    sort === 'profit' ? row.profitCents : sort === 'margin' ? row.marginPercent : row.units
  return [...rows].sort((left, right) => {
    const first = key(left)
    const second = key(right)
    if (first === null && second === null) return right.revenueCents - left.revenueCents
    if (first === null) return 1
    if (second === null) return -1
    return second - first
  })
}

export interface MarginCoverage {
  total: number
  priced: number
  noRecipe: number
  recipeUnpriced: number
  /** What the report would have called profit had the unknown costs been treated as nought. */
  unpricedRevenueCents: number
  knownProfitCents: number
  /** The share of legacy's "profit" that is really revenue with nothing subtracted. */
  overstatedShare: number | null
}

export function marginCoverage(rows: Margin[]): MarginCoverage {
  const priced = rows.filter((row) => row.state === 'priced')
  const noRecipe = rows.filter((row) => row.state === 'no-recipe')
  const recipeUnpriced = rows.filter((row) => row.state === 'recipe-unpriced')
  const unpricedRevenueCents = [...noRecipe, ...recipeUnpriced].reduce(
    (sum, row) => sum + row.revenueCents,
    0,
  )
  const knownProfitCents = priced.reduce((sum, row) => sum + (row.profitCents ?? 0), 0)
  const legacyTotal = unpricedRevenueCents + knownProfitCents
  return {
    total: rows.length,
    priced: priced.length,
    noRecipe: noRecipe.length,
    recipeUnpriced: recipeUnpriced.length,
    unpricedRevenueCents,
    knownProfitCents,
    overstatedShare: legacyTotal === 0 ? null : (unpricedRevenueCents / legacyTotal) * 100,
  }
}

// ─── cohorts ─────────────────────────────────────────────────────────────────

export interface CohortCell {
  cohort_month?: string
  cohort_size?: number
  activity_month?: string | null
  month_offset?: number | null
  active_customers?: number
}

export interface Cohort {
  month: string
  size: number
  /** Retention percentage by month offset; a missing offset is a month that has not happened yet. */
  offsets: Map<number, number>
}

export interface CohortGrid {
  cohorts: Cohort[]
  maxOffset: number
}

/**
 * The retention grid, newest cohort first.
 *
 * M0 is always 100 % by construction — everybody in a cohort was active in the month they joined — so
 * it carries no information and takes a column. It is kept, because its absence would make the grid
 * read as though the first month were missing, but it is labelled as the definition rather than a
 * measurement.
 */
export function cohortGrid(cells: CohortCell[]): CohortGrid {
  const byMonth = new Map<string, Cohort>()
  let maxOffset = 0

  for (const cell of cells) {
    const month = cell.cohort_month ?? ''
    if (month === '') continue
    const size = cell.cohort_size ?? 0
    const existing = byMonth.get(month) ?? { month, size, offsets: new Map<number, number>() }
    existing.size = size
    const offset = cell.month_offset
    if (offset !== null && offset !== undefined && size > 0) {
      existing.offsets.set(offset, ((cell.active_customers ?? 0) / size) * 100)
      if (offset > maxOffset) maxOffset = offset
    }
    byMonth.set(month, existing)
  }

  return {
    cohorts: [...byMonth.values()].sort((left, right) => right.month.localeCompare(left.month)),
    maxOffset,
  }
}

// ─── time to first order ─────────────────────────────────────────────────────

export interface Bucket {
  bucket?: string
  count?: number
}

export const TIME_BUCKETS: readonly string[] = [
  'within_1h',
  'within_24h',
  'within_7d',
  'within_30d',
  'after_30d',
  'never',
]

export interface TimeBucket {
  bucket: string
  count: number
  percent: number
}

/**
 * The buckets in order, which the payload is not in.
 *
 * It arrives `within_24h, within_7d, within_30d, within_1h, after_30d, never` — sorted by nothing —
 * so the order has to be imposed. A bucket the server omits is 0, not absent: the row belongs on a
 * distribution even when nobody is in it.
 */
export function timeBuckets(buckets: Bucket[]): { rows: TimeBucket[]; total: number } {
  const total = buckets.reduce((sum, bucket) => sum + (bucket.count ?? 0), 0)
  const rows = TIME_BUCKETS.map((name) => {
    const count = buckets.find((bucket) => bucket.bucket === name)?.count ?? 0
    return { bucket: name, count, percent: total === 0 ? 0 : (count / total) * 100 }
  })
  return { rows, total }
}

// ─── loyalty ─────────────────────────────────────────────────────────────────

export interface LoyaltyStats {
  windowDays?: number
  tierDistribution?: { loyalty_level?: string; count?: number }[]
  points?: {
    total_outstanding_points?: number
    loyalty_members?: number
    members_with_points?: number
  }
  redemptions?: {
    redemption_orders?: number
    points_redeemed?: number
    referral_points?: number
  }
}

export interface PointsPopulations {
  members: number
  withPoints: number
  /**
   * A **lower bound** on the holders of points who are not members, and labelled as one.
   *
   * The payload gives the two totals and not their overlap, so the most that can be said from it is
   * `withPoints − members`. On this data that is 925, while the database says **928** — because three
   * members hold no points, and every member without points widens the real gap. A bound is honest; a
   * subtraction presented as a count is not.
   */
  atLeastNonMembers: number
  outstanding: number
  /** True when the two counts are over different populations, which is when the pair looks impossible. */
  countsDisagree: boolean
}

/**
 * **FB-114.** *Členov 1 108* beside *S bodmi 2 033*, which reads as impossible.
 *
 * Both are right. `loyalty_members` counts `is_loyalty = true`; `members_with_points` counts
 * `points_balance > 0` across **every** customer. Measured in the database: 1 105 of the 2 033 holders
 * are members and **928 are not**, while three members hold nothing.
 * `total_outstanding_points` is over the same wider population — 250 763 points, of which members hold
 * 205 664, so **45 099 belong to non-members**. Legacy puts all three in a row of cards labelled
 * *Členov*, *S bodmi* and *Body spolu* and lets the reader make of it what they will.
 */
export function pointsPopulations(stats: LoyaltyStats | undefined): PointsPopulations {
  const members = stats?.points?.loyalty_members ?? 0
  const withPoints = stats?.points?.members_with_points ?? 0
  return {
    members,
    withPoints,
    atLeastNonMembers: Math.max(0, withPoints - members),
    outstanding: stats?.points?.total_outstanding_points ?? 0,
    countsDisagree: withPoints > members,
  }
}

export const TIER_ORDER: readonly string[] = [
  'bronze',
  'silver',
  'gold',
  'gold_master',
  'elite_master',
]

export function orderTiers(
  tiers: { loyalty_level?: string; count?: number }[],
): { level: string; count: number }[] {
  const rank = (level: string) => {
    const index = TIER_ORDER.indexOf(level)
    return index === -1 ? TIER_ORDER.length : index
  }
  return [...tiers]
    .map((tier) => ({ level: tier.loyalty_level ?? '', count: tier.count ?? 0 }))
    .sort((left, right) => rank(left.level) - rank(right.level))
}

// ─── campaigns ───────────────────────────────────────────────────────────────

export interface CampaignRow {
  type?: string
  is_active?: boolean
  sent_count?: number
  /** §10A.99 - the stale column, kept on the response and read by nothing. */
  redeemed_count?: number
  /** How many of this campaign's coupons carry a recorded origin. Nought means unmeasured. */
  tracked_coupons?: number
  /** Redemptions counted from the orders - §10A.99. */
  redeemed_actual?: number
  redemption_rate_pct?: number | string
  updated_at?: string
}

export interface CampaignRun {
  campaign_type?: string
  ran_at?: string
  recipient_count?: number
  success_count?: number
  failed_count?: number
  error_message?: string | null
  /** `ran` | `refused`. Absent on rows written before the column existed — FB-168. */
  status?: string | null
}

export interface CampaignSummary {
  campaigns: number
  active: number
  sent: number
  redeemed: number
  /** `null` when nothing can be measured — see `redemptionIsUnmeasured`. */
  ratePercent: number | null
}

/**
 * **FB-113.** Every campaign reports 0 % redemption, and cannot report anything else.
 *
 * `campaigns.redeemed_count` is written at insert and **never incremented** — grepped across every
 * module of the AGOS backend and the legacy Express one: the column appears in a SELECT alias and in
 * INSERTs, and in no UPDATE. On the restored data that is **15 campaigns, 1 657 sends, 0 redemptions,
 * every rate 0,0 %**. Legacy draws a progress bar from the rate, so fifteen empty bars read as fifteen
 * campaigns nobody used.
 *
 * A rate that cannot be anything but zero is not a measurement. It is reported as unmeasured, with the
 * send count — which *is* real — kept.
 */
export function redemptionIsUnmeasured(campaigns: CampaignRow[]): boolean {
  const sent = campaigns.reduce((sum, row) => sum + (row.sent_count ?? 0), 0)
  if (sent === 0) return false
  // §10A.99 - a campaign is measured once any of its coupons carries a recorded origin. Before
  // §10A.98 none did, so the join finds nothing and 0 % would say nobody used them.
  const tracked = campaigns.reduce((sum, row) => sum + (row.tracked_coupons ?? 0), 0)
  return tracked === 0
}

export function campaignSummary(campaigns: CampaignRow[]): CampaignSummary {
  const sent = campaigns.reduce((sum, row) => sum + (row.sent_count ?? 0), 0)
  // §10A.99 - counted from the orders through the coupon's origin, never from `redeemed_count`,
  // which FB-113 established is written at insert and never incremented.
  const redeemed = campaigns.reduce((sum, row) => sum + (row.redeemed_actual ?? 0), 0)
  return {
    campaigns: campaigns.length,
    active: campaigns.filter((row) => row.is_active === true).length,
    sent,
    redeemed,
    ratePercent: redemptionIsUnmeasured(campaigns) ? null : sent === 0 ? null : (redeemed / sent) * 100,
  }
}

/** Sent first, because a campaign that has never run is not the interesting one. */
export function orderCampaigns(campaigns: CampaignRow[]): CampaignRow[] {
  return [...campaigns].sort(
    (left, right) =>
      (right.sent_count ?? 0) - (left.sent_count ?? 0) ||
      Number(right.is_active ?? false) - Number(left.is_active ?? false) ||
      (left.type ?? '').localeCompare(right.type ?? ''),
  )
}

export type RunOutcome = 'ok' | 'failed' | 'refused'

/**
 * How a scheduler run ended.
 *
 * Legacy has two outcomes and colours anything with an `error_message` red. Three of the thirty runs
 * on this data carry *"Tento typ kampane sa nedá spustiť manuálne"* with nought recipients and nought
 * failures — somebody pressed a button on an automatic campaign. That is a refusal, not a failure, and
 * a red dot next to it sends whoever is on call looking for a broken campaign.
 */
export function runOutcome(run: CampaignRun): RunOutcome {
  // FB-168. The row says so itself now, where it has been written since the column existed. Older rows
  // are null and carry no such fact, so the inference below is what they get — it is the fallback, not
  // a second opinion.
  if (run.status === 'refused') return 'refused'
  const failed = run.failed_count ?? 0
  const message = run.error_message ?? ''
  if (failed > 0) return 'failed'
  if (message === '') return 'ok'
  return (run.recipient_count ?? 0) === 0 ? 'refused' : 'failed'
}

export function runShare(run: CampaignRun): number | null {
  const recipients = run.recipient_count ?? 0
  if (recipients === 0) return null
  return ((run.success_count ?? 0) / recipients) * 100
}

// ─── games ───────────────────────────────────────────────────────────────────

export interface GameStats {
  windowDays?: number
  miniGames?: { players?: number; converted_to_order?: number }
  wheel?: { spinners?: number; converted_to_order?: number }
}

export interface Conversion {
  players: number
  converted: number
  /** `null` when nobody played — 0 % of nobody is not a conversion rate. */
  percent: number | null
}

export function conversion(side: { players?: number; spinners?: number; converted_to_order?: number } | undefined): Conversion {
  const players = side?.players ?? side?.spinners ?? 0
  const converted = side?.converted_to_order ?? 0
  return { players, converted, percent: players === 0 ? null : (converted / players) * 100 }
}

// ─── SLA ─────────────────────────────────────────────────────────────────────

export interface SlaChannel {
  type?: string
  count?: number
  avg_minutes?: number | string
  p50_minutes?: number | string
  p95_minutes?: number | string
  min_minutes?: number | string
  max_minutes?: number | string
}

export interface SlaHour {
  hour?: number
  count?: number
  avg_minutes?: number | string
  p95_minutes?: number | string
}

export interface SlaResponse {
  windowDays?: number
  dateFrom?: string | null
  dateTo?: string | null
  byChannel?: SlaChannel[]
  byHour?: SlaHour[]
}

/** Under this, an order was not prepared and delivered — it was rung up and closed. */
export const IMPLAUSIBLE_MINUTES = 2

export interface SlaTiming {
  type: string
  count: number
  avgMinutes: number
  p50Minutes: number
  p95Minutes: number
  minMinutes: number
  maxMinutes: number
  /**
   * **FB-116.** Whether the fastest order in this channel is too fast to be one.
   *
   * The restaurant channel's minimum on the restored data is **0,06 minutes — under four seconds**
   * from *new* to *delivered*, which is a till transaction closed on the spot, not a meal. The median
   * and the p95 shrug that off; the **average** does not, and legacy shows the average first and never
   * shows the minimum, so the evidence that the average is contaminated is in the payload and off the
   * screen.
   */
  averageIsContaminated: boolean
}

export function slaTimings(channels: SlaChannel[]): SlaTiming[] {
  const rank = (type: string) => {
    const index = CHANNEL_ORDER.indexOf(type)
    return index === -1 ? CHANNEL_ORDER.length : index
  }
  return [...channels]
    .sort((left, right) => rank(left.type ?? '') - rank(right.type ?? ''))
    .map((row) => ({
      type: row.type ?? '',
      count: row.count ?? 0,
      avgMinutes: num(row.avg_minutes),
      p50Minutes: num(row.p50_minutes),
      p95Minutes: num(row.p95_minutes),
      minMinutes: num(row.min_minutes),
      maxMinutes: num(row.max_minutes),
      averageIsContaminated:
        (row.count ?? 0) > 0 && num(row.min_minutes) < IMPLAUSIBLE_MINUTES,
    }))
}

/** What window the SLA card is really over — the server says, in its own two fields. */
export function slaWindowLabel(response: SlaResponse | undefined): string {
  const from = response?.dateFrom ?? null
  const to = response?.dateTo ?? null
  if (from !== null && to !== null && from !== '' && to !== '') return `${from} → ${to}`
  return `${tt('posledných')} ${formatCount(response?.windowDays ?? 0)} ${tt('dní')}`
}

export interface HourBar {
  hour: number
  count: number
  avgMinutes: number
  p95Minutes: number
}

/** Thirteen hours come back, not twenty-four — the shop is shut for the rest. */
export function hourBars(hours: SlaHour[]): { bars: HourBar[]; peak: HourBar | null } {
  const bars = [...hours]
    .map((row) => ({
      hour: row.hour ?? 0,
      count: row.count ?? 0,
      avgMinutes: num(row.avg_minutes),
      p95Minutes: num(row.p95_minutes),
    }))
    .sort((left, right) => left.hour - right.hour)
  const peak = bars.reduce<HourBar | null>(
    (worst, bar) => (worst === null || bar.p95Minutes > worst.p95Minutes ? bar : worst),
    null,
  )
  return { bars, peak }
}

// ─── the custom pizza ────────────────────────────────────────────────────────

export interface PizzaSize {
  cm?: number
  label?: string
  basePrice?: number | string
  baseCost?: number | string | null
  enabled?: boolean
}

export interface CustomBySize {
  size_cm?: number | null
  orders?: number
  units?: number
  avg_price?: number | string
  avg_toppings_estimate?: number | string
}

export interface PizzaMarginResponse {
  windowDays?: number
  ingredients?: { id?: number; name?: string; cost_32?: number | string; cost_50?: number | string }[]
  customPizza?: { orders?: number; revenue?: number | string; avg_revenue?: number | string; units?: number }
  customBySize?: CustomBySize[]
  avgCost?: {
    avg_cost_32?: number | string
    avg_cost_50?: number | string
    ingredients_with_cost?: number
    total_active_ingredients?: number
  }
  pizzaSizes?: PizzaSize[]
}

/**
 * **FB-117.** The topping count in the payload is the constant 2, for every order and every size.
 *
 * `pizza-ingredients-margin__customBySize.sql` estimates it by counting `·` separators:
 *
 * ```sql
 * GREATEST(1, (LENGTH(product_name) - LENGTH(REPLACE(product_name, '·', ''))) + 1)
 * ```
 *
 * But the name is
 * `Vlastná pizza – 32 cm (Rajčinový základ · Mozzarella syr, Gorgonzola, Kukurica, Rajčiny, Šunka)` —
 * the `·` separates the *base* from the topping list and the toppings are separated by **commas**.
 * There is exactly one `·` in every name, so the estimate is `1 + 1` always. Measured against the real
 * comma count over a 30-day window: **4,84 toppings at 32 cm** (range 2–8) and **5,00 at 50 cm**
 * (range 2–10), against 2,00 claimed for both.
 *
 * The cost is `baseCost + avgIngredientCost × toppings`, so the cost is understated and the margin
 * overstated — 86,7 % against a corrected 82,3 % at 32 cm, 89,1 % against 86,2 % at 50 cm. The SQL was
 * character-identical to legacy's, so this was inherited rather than introduced.
 *
 * **Fixed on the server 2026-08-02** (`MIGRATION-STATUS`): the count comes from the comma-separated
 * list after the `·` now, and the live endpoint answers **4,90 at 32 cm and 5,17 at 50 cm** over 400
 * days. So this returns false on current data and the margin is shown.
 *
 * **The check stays**, and it is not dead weight: it is a detector. A payload where every size averages
 * exactly 2 is the separator bug, and if the query is ever rewritten back into it this page stops
 * presenting a margin rather than presenting a wrong one.
 */
export function toppingCountIsConstant(rows: CustomBySize[]): boolean {
  const counts = rows
    .filter((row) => (row.orders ?? 0) > 0)
    .map((row) => num(row.avg_toppings_estimate))
  if (counts.length === 0) return false
  // A genuine average over dozens of orders landing on exactly 2 at every size, every window, is the
  // separator bug and not a coincidence.
  return counts.every((count) => count === 2)
}

export interface PizzaTotals {
  orders: number
  units: number
  /** The server's own figure. Legacy re-derives it as `avg_price × units` and lands 10 cents out. */
  revenueCents: number
  /** What that re-derivation would have given, so the difference can be shown rather than argued. */
  derivedRevenueCents: number
  ingredientsPriced: number
  ingredientsTotal: number
  coveragePercent: number | null
}

/**
 * **FB-118.** The revenue is in the payload; legacy computes its own and gets a different number.
 *
 * `customPizza.revenue` is **916,20 €** over the 30-day window. Legacy sums `avg_price × units` per
 * size and displays **916,30 €**. An average is a multiplicand (FB-056): multiplying a rounded average
 * back by a count invents money, and here it invents ten cents out of nothing while the exact figure
 * sits unused two fields away.
 */
export function pizzaTotals(response: PizzaMarginResponse): PizzaTotals {
  const bySize = response.customBySize ?? []
  const priced = response.avgCost?.ingredients_with_cost ?? 0
  const total = response.avgCost?.total_active_ingredients ?? 0
  return {
    orders: response.customPizza?.orders ?? bySize.reduce((sum, row) => sum + (row.orders ?? 0), 0),
    units: response.customPizza?.units ?? bySize.reduce((sum, row) => sum + (row.units ?? 0), 0),
    revenueCents: cents(response.customPizza?.revenue),
    derivedRevenueCents: bySize.reduce(
      (sum, row) => sum + Math.round(num(row.avg_price) * (row.units ?? 0) * 100),
      0,
    ),
    ingredientsPriced: priced,
    ingredientsTotal: total,
    coveragePercent: total === 0 ? null : (priced / total) * 100,
  }
}

export interface SizeMargin {
  cm: number
  orders: number
  avgPriceCents: number
  baseCostCents: number | null
  /**
   * The per-ingredient cost, rounded **for display only**.
   *
   * FB-056: a unit cost is a multiplicand, and rounding it before multiplying invents money. Here it
   * would invent two cents — 0,1472 € × 4,84 is 71 cents, but 15 cents × 4,84 is 73 — so the
   * multiplication below uses the unrounded rate and only the product is rounded, once.
   */
  avgIngredientCents: number
  toppings: number
  /** `null` when the base cost is missing or the topping count cannot be trusted. */
  estimatedCostCents: number | null
  marginPercent: number | null
  profitCents: number | null
  /** Why the estimate is missing, so the reader is told rather than shown a dash. */
  missing: 'none' | 'base-cost' | 'ingredient-cost' | 'topping-count'
}

export function sizeMargins(response: PizzaMarginResponse): SizeMargin[] {
  const bySize = response.customBySize ?? []
  const constantToppings = toppingCountIsConstant(bySize)

  return (response.pizzaSizes ?? [])
    .filter((size) => size.enabled !== false)
    .map((size) => {
      const cm = Math.round(num(size.cm))
      const row = bySize.find((candidate) => Math.round(num(candidate.size_cm)) === cm)
      // Kept unrounded: this is a rate, and it is about to be multiplied (FB-056).
      const ingredientRate = num(cm <= 40 ? response.avgCost?.avg_cost_32 : response.avgCost?.avg_cost_50)
      const baseCostCents =
        size.baseCost === null || size.baseCost === undefined ? null : cents(size.baseCost)
      const toppings = num(row?.avg_toppings_estimate)
      const avgPriceCents = cents(row?.avg_price)
      const orders = row?.orders ?? 0

      const missing: SizeMargin['missing'] =
        baseCostCents === null || baseCostCents === 0
          ? 'base-cost'
          : ingredientRate === 0
            ? 'ingredient-cost'
            : constantToppings
              ? 'topping-count'
              : 'none'

      // FB-117: a cost built on a constant topping count is not an estimate, so no margin is shown.
      // The product of the rate and the count is rounded once, at the end.
      const estimatedCostCents =
        missing === 'none'
          ? (baseCostCents ?? 0) + Math.round(ingredientRate * toppings * 100)
          : null

      return {
        cm,
        orders,
        avgPriceCents,
        baseCostCents,
        avgIngredientCents: cents(ingredientRate),
        toppings,
        estimatedCostCents,
        marginPercent:
          estimatedCostCents === null || avgPriceCents === 0
            ? null
            : ((avgPriceCents - estimatedCostCents) / avgPriceCents) * 100,
        profitCents:
          estimatedCostCents === null ? null : (avgPriceCents - estimatedCostCents) * orders,
        missing,
      }
    })
}

export { formatCount }

/**
 * When the scheduler last did anything - `FRONTEND.md` §9.426.
 *
 * <b>The owner's report:</b> *"in Analytika - Kampane, even tho we enabled them, výkon kampaní are
 * showing "Vypnutá" and "nemerané"."* Both of those were **correct** - `is_active` is each
 * campaign's own switch and three of Šurany's fifteen are on - and the confusion is that the switch
 * they had just turned on was a different one: `AGOS_JOBS_ENABLED`, the scheduler, handed over during
 * the cutover.
 *
 * <b>Nothing exposes that flag, and this answers the question without it.</b> A screen cannot read a
 * container's environment, and adding an endpoint for it would put deployment state on a reporting
 * page. But *Posledné spustenia* already lists the scheduler's runs - it simply never said **how long
 * ago**, which is the one figure that distinguishes *the scheduler is running and these campaigns are
 * off* from *nothing is scheduled at all*.
 *
 * <b>No explanatory paragraph, deliberately.</b> §9.316 records the owner asking **twice** for the
 * panel that explained `nemerané` to be removed, and a paragraph about the scheduler would be that
 * again. One line in a subtitle that already exists.
 */
export function lastRunNote(runs: readonly CampaignRun[], now = new Date()): string {
  const newest = runs
    .map((run) => run.ran_at)
    .filter((when): when is string => typeof when === 'string' && when !== '')
    .map((when) => new Date(when).getTime())
    .filter((when) => !Number.isNaN(when))
    .reduce((latest, when) => (when > latest ? when : latest), 0)
  if (newest === 0) {
    // No run has a readable timestamp. Saying *never* would be a claim about the scheduler that the
    // rows do not support - they may simply predate the column.
    return ''
  }
  const minutes = Math.floor((now.getTime() - newest) / 60_000)
  if (minutes < 2) return tt('Naposledy spustený práve teraz')
  if (minutes < 60) return `${tt('Naposledy spustený pred')} ${formatCount(minutes)} min`
  const hours = Math.floor(minutes / 60)
  // **24 rather than 48, and the test is what found it.** With a 48-hour cutoff `days` could never
  // be 1, so the singular *dňom* was unreachable - dead code that reads as handled. And *pred 27 h*
  // is a worse answer than *pred 1 dňom* for somebody asking whether the scheduler ran yesterday.
  if (hours < 24) return `${tt('Naposledy spustený pred')} ${formatCount(hours)} h`
  const days = Math.floor(hours / 24)
  return `${tt('Naposledy spustený pred')} ${formatCount(days)} ${days === 1 ? tt('dňom') : tt('dňami')}`
}

/**
 * Whether the last run is old enough to be the answer rather than a detail - §9.426.
 *
 * Two days, because `dailyCampaigns` fires at 15:00 daily: one missed day can be a clock, two cannot.
 * Amber rather than red - a quiet scheduler is a question, not yet a fault.
 */
export function schedulerLooksStopped(
  runs: readonly CampaignRun[],
  now = new Date(),
  scheduledJobsEnabled: boolean | null = null,
): boolean {
  /* §9.536, FB-813 - **a fact beats this inference, and its blind spot was the owner's own state.** With the
     jobs flag off the scheduler cannot run at all, and `newest === 0` below answers **false** - so an
     installation whose scheduler has never run read as healthy, which is exactly what a cutover with
     `AGOS_JOBS_ENABLED=false` looks like.
     Consulted first, and an explicit `false` only: `null` is a backend older than §10A.280, where the
     heuristic is still the only answer available. §9.426's reasoning is kept rather than deleted, and the
     optional third argument leaves every existing case driving the inference unchanged. */
  if (scheduledJobsEnabled === false) return true
  const newest = runs
    .map((run) => run.ran_at)
    .filter((when): when is string => typeof when === 'string' && when !== '')
    .map((when) => new Date(when).getTime())
    .filter((when) => !Number.isNaN(when))
    .reduce((latest, when) => (when > latest ? when : latest), 0)
  if (newest === 0) return false
  return now.getTime() - newest > 48 * 60 * 60_000
}
